import createCommonModule from '@/common/store/modules/common';
import waitTask from '@/common/utils/waitTask';
import iscsiUpdater from '@/common/store/modules/iscsi/iscsiUpdater';
import { TaskState } from '@schema';
import { TaskName } from '@/common/constants/taskNames.constants';
import { STATUS_ICON_LOADING } from '@/common/utils/statuses';
import { TARGET_STATES } from '@/common/constants/iscsiTarget.constants';
import useTasksStore from '@/common/store/pinia/tasks';
import getClusterId from '@/common/store/pinia/storageCluster/getClusterId';
import targetMapper from './targetMapper';

const TPG_STATES = {
  ActiveOptimized: 'ActiveOptimized',
  ActiveNonOptimized: 'ActiveNonOptimized',
  Standby: 'Standby',
  Unavailable: 'Unavailable',
  Offline: 'Offline',
  Transitioning: 'Transitioning',
};

function resource() {
  return getClusterId()
    .then(clusterId => (`/api/v2/${clusterId}/iscsi-alua/target-groups`));
}

export default createCommonModule({
  resource,
  Model: Object,
  plural: data => data,
  config: {
    updateMethod: 'put',
  },
  getters: {
    _mapItem: (state) => (tgId) => {
      const tg = state.itemsMap[tgId];
      if (!tg) {
        return null;
      }

      const currentStateIcon = tg.running ? TARGET_STATES.running.icon : TARGET_STATES.stopped.icon;

      const tasksStore = useTasksStore();
      const isTaskRunning = tasksStore.getItems.find(
        task => task.name === TaskName.BLOCK_STORAGE_TARGET_GROUP_SET
          && task.state === TaskState.RUNNING
          && task.payload.group_id === tgId
      );

      return {
        ...tg,
        isDeletable: !!tg.id,
        statusText: tg.running ? TARGET_STATES.running.text : TARGET_STATES.stopped.text,
        statusIcon: isTaskRunning ? STATUS_ICON_LOADING : currentStateIcon
      };
    },
    getAllTargets(state, getters) {
      return getters.getItems.flatMap(tg => targetMapper(tg));
    },
    getTargetsById: (state, getters) => id => targetMapper(getters.getItemById(id)),
    getAllPath(state, getters) {
      return getters.getItems.reduce(function (memo, tg) {
        const volumesTpgs = tg.volumes
          .reduce((volMemo, v) => volMemo.concat(v.lun?.tpgs.map(tpg => ({
            ...tpg,
            lunId: v.lun?.id,
            isActive: tpg.access_state === TPG_STATES.ActiveNonOptimized || tpg.access_state === TPG_STATES.ActiveOptimized,
            volume: v,
            targetGroup: tg
          }))), []);

        return memo.concat(volumesTpgs);
      }, []);
    },
    getAllIqn: (_state, getters) => getters.getItems.flatMap((tg) => tg.targets.map(t => t.iqn))
  },
  actions: {
    CONFIGURE_AUTH(store, { targetGroupId, model }) {
      const isChap = model.chapEnabled && model.chap;
      const isAcl = model.aclEnabled && model.acl.length > 0;
      const data = {};
      let acl;

      if (!isChap && !isAcl) {
        return Promise.resolve();
      }

      if (isChap) {
        data.chap_enabled = true;
        data.chap_user = model.chap;
      }

      if (isAcl) {
        data.acl_enabled = true;
        acl = model.acl.map(it => ({
          ...it,
          luns: it.luns.map(l => +l) // convert luns to Number
        }));
      }

      return store.dispatch('CHANGE_AUTH', {
        targetGroupId,
        ...data
      })
        .then(() => Promise.all(acl
          .map(it => store.dispatch('ADD_ACL', {
            targetGroupId,
            ...it
          }))));
    },
    CHANGE_AUTH(store, { targetGroupId, ...data }) {
      const { dispatch } = store;

      return resource(store)
        .then(resourceStr => this.$api.put(`${resourceStr}/${targetGroupId}`, data))
        .then(waitTask)
        .then(() => dispatch('FETCH_ITEMS'));
    },
    ADD_ACL(store, { targetGroupId, ...data }) {
      return resource(store)
        .then(resourceStr => this.$api.post(`${resourceStr}/${targetGroupId}/acl`, data))
        .then(waitTask);
    },
    DELETE_ACL(store, { targetGroupId, wwn }) {
      return resource(store)
        .then(resourceStr => this.$api.delete(`${resourceStr}/${targetGroupId}/acl/${wwn}`))
        .then(waitTask);
    },
    EDIT_ACL(store, { targetGroupId, ...data }) {
      return resource(store)
        .then(resourceStr => this.$api.put(`${resourceStr}/${targetGroupId}/acl/${data.wwn}`, data))
        .then(waitTask);
    },
    START(store, targetGroupId) {
      return resource(store)
        .then(resourceStr => this.$api.post(`${resourceStr}/${targetGroupId}/start`));
    },
    STOP(store, targetGroupId) {
      return resource(store)
        .then(resourceStr => this.$api.post(`${resourceStr}/${targetGroupId}/stop`));
    },
    DELETE(store, { item, force }) {
      const { commit } = store;
      const { id } = item;

      return resource(store)
        .then(resourceStr => this.$api.delete(`${resourceStr}/${id}`, { force }))
        .then(waitTask)
        .then((res) => {
          commit('CLEAR_ERRORS');
          return res;
        })
        .catch((error) => {
          commit('SET_ERRORS', error);

          throw error;
        });
    },
    DELETE_ITEMS({ dispatch, commit }, { items, force }) {
      return items.reduce((promisesChain, item) => promisesChain.then(() => dispatch('DELETE', { item, force })), Promise.resolve())
        .catch((error) => {
          commit('SET_ERRORS', error);
          throw error;
        });
    },
    socket_tasks({ dispatch }, { name, result, state }) {
      const TASK_EVENT_NAMES = [
        TaskName.BLOCK_STORAGE_TARGET_GROUP_SET,
        TaskName.BLOCK_STORAGE_TARGET_GROUP_CHANGE,
        TaskName.BLOCK_STORAGE_TARGET_GROUP_DELETE
      ];
      if (TASK_EVENT_NAMES.includes(name) && state === TaskState.SUCCESS) {
        if (result?.id) {
          dispatch('FETCH_ITEM', { id: result.id });
          return;
        }
        dispatch('FETCH_ITEMS');
      }
    },
    ...iscsiUpdater('target_groups')
  }
});
